"use client";

import React from "react";
import { Award, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Compass, House as Home, Building2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function MarlowIntro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const localHighlights = [{
    title: "Riverside & High-Value Homes",
    description: "Careful access solutions for riverside residences and private estates along the Thames.",
    icon: Home
  }, {
    title: "Conservation-Sensitive Buildings",
    description: "Non-intrusive, protective setups engineered for Marlow's historic brickwork and period facades.",
    icon: Compass
  }, {
    title: "Commercial & High Street Projects",
    description: "Public-safe pavement licenses, loading bays, and edge protection for town centre retail and offices.",
    icon: Building2
  }];
  return <section id="marlowintro" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 text-foreground">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Answer-First Copy Column */}
          <div className="lg:col-span-7 space-y-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-border text-foreground text-xs font-semibold uppercase tracking-wider">
                Buckinghamshire Service Area
              </Badge>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs font-medium text-muted-foreground">Marlow &amp; Surrounds</span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Scaffolding in Marlow, built around sensitive properties.
            </motion.h2>

            <motion.div variants={itemVariants} className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p className="text-foreground font-medium text-lg sm:text-xl">
                BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Marlow and the surrounding Buckinghamshire area, including SL7.
              </p>
              <p>
                Led by Pete Wood with more than 20 years of hands-on industry experience, our team specializes in delivering reliable, safety-led access tailored to the unique architectural landscape of the town.
              </p>
              <p>
                Whether you are managing complex roof works on period riverside properties, restoring conservation-sensitive heritage buildings, or undertaking major structural renovations on high-value residential homes, we ensure clean setups with minimal intrusion and total site safety.
              </p>
            </motion.div>

            {/* Accreditations & Experience Badges */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
              <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3.5 py-2 border border-border/70 text-foreground text-sm font-medium">
                <ShieldCheck className="size-4 text-primary shrink-0" />
                <span>NASC Audited</span>
              </div>
              <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3.5 py-2 border border-border/70 text-foreground text-sm font-medium">
                <Award className="size-4 text-primary shrink-0" />
                <span>CHAS Accredited</span>
              </div>
              <div className="flex items-center gap-2 rounded-md bg-muted/60 px-3.5 py-2 border border-border/70 text-foreground text-sm font-medium">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Pete Wood (20+ Years Exp.)</span>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a Marlow quote</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted">
                <Link to="/ProjectsCaseStudiesPage">
                  <span>View local projects</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Feature Column / Local Detail Cards */}
          <motion.div variants={itemVariants} className="lg:col-span-5 space-y-4">
            <Card className="border border-border bg-card shadow-sm">
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div className="space-y-1">
                  <h3 className="font-default text-lg font-semibold text-card-foreground">
                    Marlow access capabilities
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Specialist scaffolding designed for Buckinghamshire property types.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-4">
                  {localHighlights.map((item, i) => {
                  const IconComponent = item.icon;
                  return <div key={item.title} data-index={i} className="flex items-start gap-3.5">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary mt-0.5">
                          <IconComponent className="size-4" />
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-sm font-semibold text-foreground">
                            {item.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                            {item.description}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="rounded-md bg-muted/40 p-4 border border-border text-xs text-muted-foreground leading-relaxed">
                  Fully insured with qualified CISRS scaffolders operating to TG20:21 compliance across Marlow and the Thames Valley.
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}